/**
 * Rebase WebM/Matroska cluster timestamps to zero.
 *
 * WHY THIS EXISTS
 * ---------------
 * Chrome on Android's MediaRecorder writes the CAPTURE CLOCK into cluster
 * timestamps and never rebases them to zero. A 3-minute recording made on a
 * device that had been up ~96 minutes produces a file whose timeline runs from
 * 5795s to 5975s. Firefox and Zen rebase, which is why only Chrome-Android was
 * affected.
 *
 * The browser honours those timestamps literally, so `<audio>` reports a
 * 1:39:36 duration with the audio sitting near the end — the user sees a
 * 100-minute scrubber for a 3-minute clip (prod, 2026-08-05). No amount of
 * metadata correction fixes this: the player reads the container, not our
 * database.
 *
 * WHY ON READ, IN THE BROWSER
 * ---------------------------
 * The stored blob is encrypted under the user's session DEK, so only the
 * backend could rewrite it at rest — and the backend image has no ffmpeg. The
 * ASR worker has ffmpeg but no access to the blob store, and returning
 * normalised audio through its Redis envelope does not scale (up to ~200 MB at
 * the 4h cap). Normalising on read costs one pass over an already-in-memory
 * buffer and, decisively, repairs recordings that were ALREADY captured —
 * ingest-time normalisation could only ever help future ones.
 *
 * WHAT IT DOES
 * ------------
 * Walks the EBML tree, collects every Cluster's Timecode (0xE7), and subtracts
 * the smallest one from all of them, so the timeline starts at zero. Block
 * timestamps inside a cluster are relative to that cluster's Timecode, so they
 * stay correct untouched. Segment Info Duration (0x4489) is reduced by the same
 * base so the reported length becomes the content length.
 *
 * Values are rewritten IN PLACE at their original byte width. That is safe
 * because we only ever subtract — the new value is smaller, and EBML unsigned
 * integers may carry leading zeros — so no element size changes and no offset
 * in the file moves.
 *
 * SAFETY
 * ------
 * Every failure path returns null and the caller uses the original bytes. A
 * file we cannot confidently parse is never modified.
 */

/** Matroska element IDs, as their full encoded form. */
const ID_SEGMENT = 0x18538067;
const ID_CLUSTER = 0x1f43b675;
const ID_TIMECODE = 0xe7;
const ID_INFO = 0x1549a966;
const ID_DURATION = 0x4489;
const ID_TIMECODE_SCALE = 0x2ad7b1;

/** Elements we descend into rather than skip over. */
const CONTAINERS = new Set<number>([ID_SEGMENT, ID_CLUSTER, ID_INFO]);

interface Cursor {
  readonly view: DataView;
  pos: number;
}

/** Reads an EBML element ID (length encoded in the leading byte). */
function readId(c: Cursor): number | null {
  if (c.pos >= c.view.byteLength) return null;
  const first = c.view.getUint8(c.pos);
  if (first === 0) return null;
  let width = 1;
  for (let mask = 0x80; mask > 0; mask >>= 1, width++) {
    if (first & mask) break;
  }
  if (width > 4 || c.pos + width > c.view.byteLength) return null;
  let id = 0;
  for (let i = 0; i < width; i++) id = id * 256 + c.view.getUint8(c.pos + i);
  c.pos += width;
  return id;
}

/**
 * Reads an EBML data size. Returns the size plus whether it was the
 * "unknown size" sentinel (all value bits set) — live-muxed WebM uses that for
 * Segment and sometimes Cluster, so it must be handled, not treated as an error.
 */
function readSize(c: Cursor): { size: number; unknown: boolean } | null {
  if (c.pos >= c.view.byteLength) return null;
  const first = c.view.getUint8(c.pos);
  if (first === 0) return null;
  let width = 1;
  let mask = 0x80;
  while (width <= 8 && !(first & mask)) {
    mask >>= 1;
    width++;
  }
  if (width > 8 || c.pos + width > c.view.byteLength) return null;
  let value = first & (mask - 1);
  let allOnes = value === mask - 1;
  for (let i = 1; i < width; i++) {
    const b = c.view.getUint8(c.pos + i);
    if (b !== 0xff) allOnes = false;
    value = value * 256 + b;
  }
  c.pos += width;
  return { size: value, unknown: allOnes };
}

/** Reads a big-endian unsigned integer of `len` bytes. */
function readUint(view: DataView, pos: number, len: number): number {
  let v = 0;
  for (let i = 0; i < len; i++) v = v * 256 + view.getUint8(pos + i);
  return v;
}

/** Writes a big-endian unsigned integer into exactly `len` bytes. */
function writeUint(view: DataView, pos: number, len: number, value: number): void {
  let v = Math.max(0, Math.round(value));
  for (let i = len - 1; i >= 0; i--) {
    view.setUint8(pos + i, v % 256);
    v = Math.floor(v / 256);
  }
}

interface Found {
  /** Cluster Timecode elements: byte offset + width, so we can rewrite in place. */
  timecodes: Array<{ pos: number; len: number; value: number }>;
  /** Segment Info Duration (a float), if present. */
  duration: { pos: number; len: number; value: number } | null;
  timecodeScale: number | null;
}

/** Depth-first walk collecting the elements we intend to rewrite. */
function collect(view: DataView, start: number, end: number, out: Found, depth = 0): void {
  if (depth > 8) return; // Matroska nests shallowly; guard against a malformed file.
  const c: Cursor = { view, pos: start };
  while (c.pos < end) {
    const elemStart = c.pos;
    const id = readId(c);
    if (id === null) return;
    const size = readSize(c);
    if (size === null) return;
    const dataStart = c.pos;
    // An unknown-size element runs to the end of its parent — normal for
    // live-muxed Segment/Cluster, which is exactly the MediaRecorder case.
    const dataEnd = size.unknown ? end : Math.min(dataStart + size.size, end);
    if (dataEnd < dataStart || elemStart === dataStart) return;

    if (CONTAINERS.has(id)) {
      collect(view, dataStart, dataEnd, out, depth + 1);
    } else if (id === ID_TIMECODE) {
      const len = dataEnd - dataStart;
      if (len > 0 && len <= 8) {
        out.timecodes.push({ pos: dataStart, len, value: readUint(view, dataStart, len) });
      }
    } else if (id === ID_DURATION) {
      const len = dataEnd - dataStart;
      if (len === 4 || len === 8) {
        const value = len === 4 ? view.getFloat32(dataStart) : view.getFloat64(dataStart);
        out.duration = { pos: dataStart, len, value };
      }
    } else if (id === ID_TIMECODE_SCALE) {
      const len = dataEnd - dataStart;
      if (len > 0 && len <= 8) out.timecodeScale = readUint(view, dataStart, len);
    }
    c.pos = dataEnd;
  }
}

/**
 * Returns a rebased copy of `input`, or null when the file needs no change or
 * cannot be confidently parsed (caller should then use the original bytes).
 *
 * @param minOffsetMs only rebase when the first cluster starts at least this
 *   far in, so ordinary files — and webm's small negative/zero start — are left
 *   completely alone.
 */
export function rebaseWebmTimestamps(input: ArrayBuffer, minOffsetMs = 1000): ArrayBuffer | null {
  try {
    if (input.byteLength < 4) return null;
    const probe = new DataView(input);
    // EBML magic 0x1A45DFA3. Anything else is not Matroska/WebM — leave it.
    if (probe.getUint32(0) !== 0x1a45dfa3) return null;

    const copy = input.slice(0);
    const view = new DataView(copy);
    const found: Found = { timecodes: [], duration: null, timecodeScale: null };
    collect(view, 0, copy.byteLength, found);

    if (found.timecodes.length === 0) return null;
    const base = Math.min(...found.timecodes.map((t) => t.value));
    if (!Number.isFinite(base) || base <= 0) return null;

    // TimecodeScale is ns per tick (default 1,000,000 = 1ms).
    const scaleNs = found.timecodeScale && found.timecodeScale > 0 ? found.timecodeScale : 1_000_000;
    const baseMs = (base * scaleNs) / 1_000_000;
    if (baseMs < minOffsetMs) return null;

    for (const t of found.timecodes) {
      writeUint(view, t.pos, t.len, t.value - base);
    }
    if (found.duration && found.duration.value > base) {
      const next = found.duration.value - base;
      if (found.duration.len === 4) view.setFloat32(found.duration.pos, next);
      else view.setFloat64(found.duration.pos, next);
    }
    return copy;
  } catch {
    // Never let a parsing bug cost the user their audio.
    return null;
  }
}

/**
 * Convenience wrapper: rebase if needed, otherwise hand back the original blob.
 * Preserves the MIME type either way so `<audio>` still picks the right decoder.
 */
export async function normalizeRecordingBlob(blob: Blob): Promise<Blob> {
  // Only Matroska/WebM carries this defect; skip the read for anything else.
  if (blob.type && !/webm|matroska|octet-stream/i.test(blob.type)) return blob;
  try {
    const buf = await blob.arrayBuffer();
    const fixed = rebaseWebmTimestamps(buf);
    return fixed ? new Blob([fixed], { type: blob.type || 'video/webm' }) : blob;
  } catch {
    return blob;
  }
}
